import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCurrency } from '@/hooks/useCurrency';
import { ABC_XYZ_KEYS, type AbcXyzKey } from '@/services/safetyStockService';
import type { SafetyStockSummaryRow } from '@/services/safetyStockService';
import {
  formatDays,
  formatPercentFraction,
  toNumber,
} from '../utils/format';

type MatrixMetric = 'value' | 'days' | 'pctProtection';

const ABC = ['A', 'B', 'C'] as const;
const XYZ = ['X', 'Y', 'Z'] as const;

interface Props {
  rows: SafetyStockSummaryRow[];
  loading?: boolean;
  disabled?: boolean;
}

function cellBg(ratio: number | null): string {
  if (ratio == null || Number.isNaN(ratio)) return 'bg-gray-50';
  if (ratio >= 0.75) return 'bg-sky-200/80';
  if (ratio >= 0.45) return 'bg-sky-100';
  if (ratio >= 0.2) return 'bg-sky-50';
  return 'bg-white';
}

export function AbcXyzMatrix({ rows, loading, disabled }: Props) {
  const { t, i18n } = useTranslation();
  const { formatRounded, symbol } = useCurrency();
  const navigate = useNavigate();
  const locale = i18n.language || 'es';
  const [metric, setMetric] = useState<MatrixMetric>('value');

  const byKey = useMemo(() => {
    const map = new Map<string, SafetyStockSummaryRow>();
    for (const row of rows) {
      const key = String(row.dimension_value || '').toUpperCase();
      if (ABC_XYZ_KEYS.includes(key as AbcXyzKey)) {
        map.set(key, row);
      }
    }
    return map;
  }, [rows]);

  const metricValues = useMemo(() => {
    const vals: number[] = [];
    for (const key of ABC_XYZ_KEYS) {
      const row = byKey.get(key);
      if (!row) continue;
      let n: number | null = null;
      if (metric === 'value') n = toNumber(row.safety_stock_value);
      else if (metric === 'days') {
        n = toNumber(
          row.coverage_days_median ?? row.coverage_days_units ?? row.coverage_days_value,
        );
      } else {
        n = toNumber(row.ss_pct_protection_value ?? row.ss_pct_protection_units);
      }
      if (n != null) vals.push(n);
    }
    const max = vals.length ? Math.max(...vals) : 0;
    return { max };
  }, [byKey, metric]);

  const renderValue = (row: SafetyStockSummaryRow | undefined) => {
    if (!row) return '—';
    if (metric === 'value') {
      const n = toNumber(row.safety_stock_value);
      return n == null ? '—' : formatRounded(n);
    }
    if (metric === 'days') {
      return formatDays(
        row.coverage_days_median ?? row.coverage_days_units ?? row.coverage_days_value,
        locale,
      );
    }
    if (
      row.no_data_reasons?.ss_pct_protection_value ||
      row.no_data_reasons?.ss_pct_protection_units
    ) {
      return '—';
    }
    return formatPercentFraction(
      row.ss_pct_protection_value ?? row.ss_pct_protection_units,
      locale,
    );
  };

  const ratioOf = (row: SafetyStockSummaryRow | undefined): number | null => {
    if (!row || metricValues.max <= 0) return null;
    let n: number | null = null;
    if (metric === 'value') n = toNumber(row.safety_stock_value);
    else if (metric === 'days') {
      n = toNumber(
        row.coverage_days_median ?? row.coverage_days_units ?? row.coverage_days_value,
      );
    } else n = toNumber(row.ss_pct_protection_value ?? row.ss_pct_protection_units);
    if (n == null) return null;
    return n / metricValues.max;
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {t('safetyStock.summary.matrix.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('safetyStock.summary.matrix.help')}
          </p>
          <p className="mt-1 text-xs font-medium text-gray-600">
            {t('safetyStock.summary.matrix.abcBasis')}
          </p>
        </div>
        <div className="inline-flex rounded-md border border-gray-200 p-0.5 text-sm">
          {(
            [
              ['value', 'safetyStock.summary.matrix.metrics.value'],
              ['days', 'safetyStock.summary.matrix.metrics.days'],
              ['pctProtection', 'safetyStock.summary.matrix.metrics.pctProtection'],
            ] as const
          ).map(([id, labelKey]) => (
            <button
              key={id}
              type="button"
              disabled={disabled}
              className={[
                'rounded px-3 py-1.5 transition-colors',
                metric === id
                  ? 'bg-stocker-primary text-white'
                  : 'text-gray-600 hover:bg-gray-50',
              ].join(' ')}
              onClick={() => setMetric(id)}
            >
              {t(labelKey, { currencySymbol: symbol })}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('safetyStock.summary.loading')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">
                  ABC \\ XYZ
                </th>
                {XYZ.map((z) => (
                  <th
                    key={z}
                    className="px-2 py-1.5 text-center text-xs font-semibold text-gray-700"
                  >
                    {z}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ABC.map((a) => (
                <tr key={a}>
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">
                    {a}
                  </th>
                  {XYZ.map((z) => {
                    const key = `${a}${z}` as AbcXyzKey;
                    const row = byKey.get(key);
                    const ratio = ratioOf(row);
                    return (
                      <td key={key} className="px-1.5 py-1.5">
                        <button
                          type="button"
                          disabled={disabled || !row}
                          className={[
                            'w-full rounded-md border border-gray-200 px-2 py-3 text-center transition-shadow',
                            cellBg(ratio),
                            row
                              ? 'hover:shadow-sm hover:ring-1 hover:ring-stocker-primary/40'
                              : 'cursor-default opacity-60',
                          ].join(' ')}
                          onClick={() => {
                            if (!row) return;
                            navigate(`/safety-stock/productos?abc_xyz=${key}`);
                          }}
                        >
                          <div className="text-[11px] font-semibold text-gray-500">{key}</div>
                          <div className="mt-1 text-sm font-semibold tabular-nums text-gray-900">
                            {renderValue(row)}
                          </div>
                          {row ? (
                            <div className="mt-0.5 text-[11px] text-gray-500">
                              {t('safetyStock.summary.matrix.skus', { count: row.items ?? 0 })}
                            </div>
                          ) : null}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
